<script setup>
import { ref, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "@/store/user-store";
import Header from "@/components/landing/LandingHeaderNew.vue";
import HeroSection from "@/sections/landing/HeroSection.vue";
import ValueCards from "@/sections/landing/ValueCards.vue";
import HowItWorks from "@/sections/landing/HowItWorks.vue";
import FounderMessage from "@/sections/landing/FounderMessage.vue";
import CapabilitiesGrid from "@/sections/landing/CapabilitiesGrid.vue";
import RiskAssessmentSection from "@/sections/landing/RiskAssessmentSection.vue";
import FinancialCalculatorsSection from "@/sections/landing/FinancialCalculatorsSection.vue";
import Subscription from "@/sections/landing/AnSubscription.vue";
import FAQSection from "@/sections/landing/FAQSection.vue";
import GetInTouch from "@/sections/landing/GetInTouch.vue";
import AuthenticationPage from "@/utility/AuthenticationPage.vue";
import LandingFooter from "@/sections/landing/LandingFooter.vue";

const route = useRoute();
const authEnabled = ref(false);

const setAuth = (flag, view = 'signin') => {
  authEnabled.value = flag;

  // Set the auth view if opening the modal
  if (flag && view) {
    const userStore = useUserStore();
    userStore.setAuthView(view);
  }

  // Use requestAnimationFrame and ensure proper cleanup to avoid scroll-blocking issues
  if (flag) {
    // When opening modal, prevent body scroll
    requestAnimationFrame(() => {
      document.body.style.overflow = "hidden";
    });
  } else {
    // When closing modal, restore scroll with a slight delay to ensure smooth transition
    requestAnimationFrame(() => {
      document.body.style.overflow = "";
    });
  }
};

// Watch for query parameters to open auth modal
watch(() => route.query, (query) => {
  if (query.openAuth === 'true' && query.authView) {
    // Open auth modal with specified view
    setAuth(true, query.authView);
  }
}, { immediate: true });

// Ensure body overflow is reset when component unmounts
onUnmounted(() => {
  document.body.style.overflow = "";
});
</script>

<template>
  <!-- Authentication Modal (unchanged) -->
  <AuthenticationPage :authEnabled="authEnabled" :setAuth="setAuth" />

  <!-- Header with Persona Toggle -->
  <Header :setAuth="setAuth" />

  <!-- Main Content -->
  <main>
    <!-- Hero Section (persona-driven) -->
    <HeroSection :setAuth="setAuth" />

    <!-- What You Get (3 value cards) -->
    <ValueCards />

    <!-- How It Works (4-step journey) -->
    <HowItWorks />

    <!-- Founder's Message -->
    <FounderMessage />

    <!-- Platform Capabilities -->
    <CapabilitiesGrid />

    <!-- Risk Assessment Tool (preserved from original) -->
    <RiskAssessmentSection />

    <!-- Financial Calculators (preserved from original) -->
    <FinancialCalculatorsSection />

    <!-- Pricing / Subscription (preserved from original, repositioned) -->
    <Subscription :set-auth="setAuth" />

    <!-- FAQ Section (persona-driven) -->
    <FAQSection />

    <!-- Contact Section (preserved from original) -->
    <GetInTouch />
  </main>

  <!-- Footer -->
  <LandingFooter />
</template>

<style scoped>
main {
  overflow-x: hidden;
  width: 100%;
}

/* Aggressively reduce vertical spacing between sections */
main > * {
  margin: 0 !important;
  padding-top: 30px !important;
  padding-bottom: 30px !important;
}

/* Remove extra spacing from first section */
main > *:first-child {
  padding-top: 0 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  main > * {
    padding-top: 20px !important;
    padding-bottom: 20px !important;
  }
}

@media (max-width: 480px) {
  main > * {
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }
}
</style>
